import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { Market } from '../../features/markets/types/Market'
import { readMarketNames } from '../features/markets/helpers/marketHelpers'
import { InternalMarket, MarketMutation, SavedMarket } from '../features/markets/types/InternalMarket'
import { optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'
import { LanguageMaps } from './language'

const internalMarketsListApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalMarkets', 'languageMaps'] }).injectEndpoints({
  endpoints: (builder) => ({
    // Raw markets, names not localised.
    getInternalMarkets: builder.query<Market[], void>({
      query: () => 'markets',
      providesTags: ['internalMarkets'],
    }),
    // The backend writes `names` into the `markets` language group; both are read back to confirm what was stored.
    saveMarket: builder.mutation<SavedMarket, MarketMutation>({
      async queryFn(market, _api, _extraOptions, baseQuery) {
        const saved =
          'id' in market
            ? await baseQuery({ url: `i/markets/${market.id}`, method: 'PUT', body: market, responseHandler: optionalJson })
            : await baseQuery({ url: 'i/markets', method: 'POST', body: market, responseHandler: optionalJson })
        if (saved.error) return { error: saved.error }
        const [markets, languageMaps] = await Promise.all([baseQuery('markets'), baseQuery('language')])
        if (markets.error || languageMaps.error) return { error: (markets.error ?? languageMaps.error) as FetchBaseQueryError }
        const returned = saved.data as InternalMarket | null
        const stored = (markets.data as InternalMarket[]).find((candidate) => (returned?.id ? candidate.id === returned.id : candidate.iso === market.iso))
        return { data: { market: stored, names: readMarketNames(languageMaps.data as LanguageMaps, stored?.iso ?? market.iso) } }
      },
      invalidatesTags: ['internalMarkets', 'languageMaps'],
    }),
    // Publishing can't be undone. Returns the market as listed afterwards.
    publishMarket: builder.mutation<InternalMarket | undefined, string>({
      async queryFn(iso, _api, _extraOptions, baseQuery) {
        const published = await baseQuery({
          url: `i/markets/publish/${encodeURIComponent(iso)}`,
          method: 'PUT',
          body: { marketIso: iso },
          responseHandler: optionalJson,
        })
        if (published.error) return { error: published.error }
        const markets = await baseQuery('markets')
        if (markets.error) return { error: markets.error }
        return { data: (markets.data as InternalMarket[]).find((market) => market.iso === iso) }
      },
      invalidatesTags: ['internalMarkets'],
    }),
  }),
})

export const { useGetInternalMarketsQuery, useSaveMarketMutation, usePublishMarketMutation } = internalMarketsListApi
